import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, Button, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, Chip, IconButton, InputAdornment, Switch, Tabs, Tab,
  FormControl, InputLabel, Select, MenuItem, Checkbox, ListItemText, OutlinedInput
} from '@mui/material';
import { Add, Edit, People, AttachMoney, Visibility, Delete } from '@mui/icons-material';
import { api } from '../api';

export default function StudentsPage() {
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deletingStudent, setDeletingStudent] = useState(null);
  const [editingStudent, setEditingStudent] = useState(null);
  const [form, setForm] = useState({ name: '', rate_per_class: '' });
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0); // 0 = Active, 1 = Archived
  const navigate = useNavigate();

  const fetchData = async () => {
    try {
      const [studentsData, classesData] = await Promise.all([api.getStudents(), api.getClasses()]);
      setStudents(studentsData);
      setClasses(classesData.filter(c => c.is_active));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const activeStudents = students.filter(s => s.is_active);
  const archivedStudents = students.filter(s => !s.is_active);
  const displayedStudents = tab === 0 ? activeStudents : archivedStudents;

  const openDialog = async (student = null) => {
    if (student) {
      setEditingStudent(student);
      setForm({ name: student.name, rate_per_class: student.rate_per_class?.toString() || '' });
      try {
        const detail = await api.getStudent(student.id);
        setSelectedClasses(detail.enrollments.map(e => e.class_id));
      } catch { setSelectedClasses([]); }
    } else {
      setEditingStudent(null);
      setForm({ name: '', rate_per_class: '' });
      setSelectedClasses([]);
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const payload = {
      name: form.name,
      rate_per_class: parseFloat(form.rate_per_class) || 0
    };
    try {
      let studentId;
      if (editingStudent) {
        await api.updateStudent(editingStudent.id, payload);
        studentId = editingStudent.id;
      } else {
        const created = await api.createStudent(payload);
        studentId = created.id;
      }
      // Handle enrollments
      if (editingStudent) {
        const detail = await api.getStudent(studentId);
        const currentEnrollments = detail.enrollments.map(e => e.class_id);
        for (const classId of currentEnrollments) {
          if (!selectedClasses.includes(classId)) {
            await api.unenrollStudent(studentId, classId);
          }
        }
        for (const classId of selectedClasses) {
          if (!currentEnrollments.includes(classId)) {
            await api.enrollStudent(studentId, { class_id: classId });
          }
        }
      } else {
        for (const classId of selectedClasses) {
          await api.enrollStudent(studentId, { class_id: classId });
        }
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleToggle = async (student) => {
    await api.toggleStudent(student.id);
    fetchData();
  };

  const handleDelete = async () => {
    if (!deletingStudent) return;
    try {
      await api.deleteStudent(deletingStudent.id);
      setConfirmDeleteOpen(false);
      setDeletingStudent(null);
      fetchData();
    } catch (err) {
      alert(err.message);
    }
  };

  return (
    <Box sx={{ p: 2, maxWidth: 600, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h5" fontWeight={700}>Students</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => openDialog()}>
          New Student
        </Button>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label={`Active (${activeStudents.length})`} />
        <Tab label={`Archived (${archivedStudents.length})`} />
      </Tabs>

      {displayedStudents.length === 0 && !loading && (
        <Card variant="outlined" sx={{ textAlign: 'center', py: 4 }}>
          <People sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography color="text.secondary">
            {tab === 0 ? 'No active students. Add your first student!' : 'No archived students.'}
          </Typography>
        </Card>
      )}

      {displayedStudents.map(student => (
        <Card key={student.id} sx={{ mb: 1.5 }}>
          <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box sx={{ flex: 1, cursor: 'pointer' }} onClick={() => navigate(`/students/${student.id}`)}>
                <Typography variant="h6" sx={{ fontSize: '1.05rem' }}>{student.name}</Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                  <Chip size="small" icon={<AttachMoney />} label={`$${student.rate_per_class}/class`} variant="outlined" />
                  {student.balance_owed > 0 && (
                    <Chip size="small" label={`Owes $${student.balance_owed.toFixed(2)}`} color="error" variant="outlined" />
                  )}
                  {student.balance_owed === 0 && student.total_charges > 0 && (
                    <Chip size="small" label="Paid up" color="success" variant="outlined" />
                  )}
                  {student.balance_owed < 0 && (
                    <Chip size="small" label={`Credit $${Math.abs(student.balance_owed).toFixed(2)}`} color="info" variant="outlined" />
                  )}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <IconButton size="small" onClick={() => navigate(`/students/${student.id}`)}><Visibility fontSize="small" /></IconButton>
                <IconButton size="small" onClick={() => openDialog(student)}><Edit fontSize="small" /></IconButton>
                <IconButton size="small" onClick={() => { setDeletingStudent(student); setConfirmDeleteOpen(true); }} title="Delete" color="error"><Delete fontSize="small" /></IconButton>
                <Switch size="small" checked={!!student.is_active} onChange={() => handleToggle(student)} title={student.is_active ? 'Archive' : 'Restore'} />
              </Box>
            </Box>
          </CardContent>
        </Card>
      ))}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editingStudent ? 'Edit Student' : 'New Student'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth label="Student Name" value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
            sx={{ mt: 1, mb: 2 }} autoFocus required
          />
          <TextField
            fullWidth label="Rate per Class" value={form.rate_per_class}
            onChange={e => setForm({ ...form, rate_per_class: e.target.value })}
            type="number" sx={{ mb: 2 }}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
          />
          <FormControl fullWidth>
            <InputLabel>Enroll in Classes</InputLabel>
            <Select
              multiple value={selectedClasses}
              onChange={e => setSelectedClasses(e.target.value)}
              input={<OutlinedInput label="Enroll in Classes" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map(id => {
                    const cls = classes.find(c => c.id === id);
                    return <Chip key={id} label={cls?.name || id} size="small" />;
                  })}
                </Box>
              )}
            >
              {classes.map(cls => (
                <MenuItem key={cls.id} value={cls.id}>
                  <Checkbox checked={selectedClasses.includes(cls.id)} />
                  <ListItemText primary={cls.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.name.trim()}>
            {editingStudent ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)}>
        <DialogTitle>Delete Student</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to permanently delete <strong>{deletingStudent?.name}</strong>?
            This will also delete all their attendance records, payments, and enrollments.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
